import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL

function AccessLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState({
    admin_username: '',
    access_type: '',
    status: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [pagination.page, filters]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    const apiKey = localStorage.getItem('apiKey');
    
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    } else if (apiKey) {
      return { 'X-API-Key': apiKey };
    }
    return {};
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await axios.get(`${API_BASE_URL}/access-logs`, {
        params,
        headers: getAuthHeaders()
      });
      
      setLogs(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/access-logs/stats`, {
        params: { days: 7 },
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await axios.get(`${API_BASE_URL}/access-logs/export`, {
        params,
        headers: getAuthHeaders()
      });
      
      setExportData(response.data);
      setShowExport(true);
    } catch (error) {
      console.error('Error exporting logs:', error);
      alert('Failed to export logs');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const downloadJSON = () => {
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `access_logs_${new Date().toISOString()}.json`;
    link.click();
  };

  return (
    <div>
      <div className="page-header">
        <h1>Access Logs</h1>
        <p>접속 기록 보관 (개인정보보호법 제8조 - 1년 보관)</p>
        <button 
          className="btn btn-success" 
          onClick={handleExport}
          style={{ marginTop: '10px' }}
        >
          📥 Export Logs (Compliance)
        </button>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Logs (7 days)</h3>
            <div className="value">{stats.total_logs.toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <h3>Unique Admins</h3>
            <div className="value">{stats.by_admin.length}</div>
          </div>
          <div className="stat-card">
            <h3>Access Types</h3>
            <div className="value">{stats.by_type.length}</div>
          </div>
          <div className="stat-card">
            <h3>Success Rate</h3>
            <div className="value">
              {stats.by_status.find(s => s.status === 'success')?.count || 0} / {stats.total_logs}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>Access Log Filters</h2>
        </div>

        <div className="filters">
          <div className="filter-item">
            <input
              type="text"
              className="form-control"
              placeholder="Admin username"
              value={filters.admin_username}
              onChange={(e) => handleFilterChange('admin_username', e.target.value)}
            />
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.access_type}
              onChange={(e) => handleFilterChange('access_type', e.target.value)}
            >
              <option value="">All Access Types</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="api_access">API Access</option>
              <option value="user_view">User View</option>
              <option value="user_edit">User Edit</option>
              <option value="serial_key_create">Serial Key Create</option>
              <option value="serial_key_reset">Serial Key Reset</option>
              <option value="permission_grant">Permission Grant</option>
              <option value="permission_revoke">Permission Revoke</option>
            </select>
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              className="form-control"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              className="form-control"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Access Log Records</h2>
          <p style={{ fontSize: '14px', color: '#7f8c8d', margin: '5px 0 0 0' }}>
            계정, 일시, IP주소, 처리한 업무 (Required by law)
          </p>
        </div>
        
        {loading ? (
          <div className="loading">Loading access logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No access logs found
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>계정 (Account)</th>
                    <th>일시 (DateTime)</th>
                    <th>IP주소 (IP)</th>
                    <th>업무구분 (Type)</th>
                    <th>Endpoint</th>
                    <th>Target</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.admin_username || '-'}</td>
                      <td style={{ fontSize: '12px' }}>{new Date(log.created_at).toLocaleString()}</td>
                      <td style={{ fontSize: '11px' }}>{log.ip_address || '-'}</td>
                      <td>
                        <span className="badge badge-info" style={{ fontSize: '11px' }}>
                          {log.access_type}
                        </span>
                      </td>
                      <td style={{ fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.endpoint}
                      </td>
                      <td style={{ fontSize: '11px' }}>{log.target_resource || '-'}</td>
                      <td>
                        <span className={`badge ${
                          log.status === 'success' ? 'badge-success' :
                          log.status === 'failed' ? 'badge-danger' : 'badge-warning'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Export Modal */}
      {showExport && exportData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%'
          }}>
            <h2>Export Access Logs</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Compliance Export (개인정보보호법 제8조)
            </p>

            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
              <p><strong>Total Records:</strong> {exportData.total}</p>
              <p><strong>Export Date:</strong> {new Date(exportData.export_date).toLocaleString()}</p>
              <p><strong>Compliance:</strong> {exportData.compliance}</p>
            </div>

            <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
              <strong>⚠️ Important:</strong>
              <ul style={{ marginTop: '10px', marginBottom: 0, paddingLeft: '20px' }}>
                <li>Access logs contain sensitive information</li>
                <li>Handle exported data securely</li>
                <li>Do not share with unauthorized personnel</li>
                <li>Logs must be retained for 1 year per regulation</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={downloadJSON}>
                📥 Download JSON
              </button>
              <button className="btn btn-secondary" onClick={() => setShowExport(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
        <h3>🔒 Compliance Information (개인정보보호법 제8조)</h3>
        <p style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.6' }}>
          <strong>Required Access Log Fields:</strong>
        </p>
        <ul style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.8' }}>
          <li>✅ 계정 (Account): Admin username logged</li>
          <li>✅ 일시 (DateTime): Timestamp of each access</li>
          <li>✅ IP주소 (IP Address): Client IP tracked</li>
          <li>✅ 처리한 업무 (Task): Access type and endpoint</li>
          <li>✅ 1 year retention period enforced</li>
        </ul>
      </div>
    </div>
  );
}

export default AccessLogs;